import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useLang, LangToggle } from '../LanguageContext';
import { t } from '../i18n';

export default function FicheClient({ client, user, isManager, onClose }) {
  const [visites, setVisites] = useState([]);
  const [actions, setActions] = useState([]);
  const [onglet, setOnglet] = useState('infos');
  const [loading, setLoading] = useState(true);
  const { lang, isRTL } = useLang();
  const [editMode, setEditMode] = useState(false);
  const [visiteASupprimer, setVisiteASupprimer] = useState(null);
  const [form, setForm] = useState({
    nom: client.nom || '',
    telephone: client.telephone || '',
    adresse: client.adresse || '',
    commune: client.commune || '',
    wilaya: client.wilaya || '',
    type: client.type || client.statut || 'Prospect',
    potentiel: client.potentiel || 'Moyen',
  });

  useEffect(() => { charger(); }, []);

  const charger = async () => {
    setLoading(true);
    try {
      const vSnap = await getDocs(query(collection(db, 'visites'), where('clientId', '==', client.id)));
      const vs = vSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => new Date(b.heureDebut || 0) - new Date(a.heureDebut || 0));
      setVisites(vs);
      const aSnap = await getDocs(query(collection(db, 'actions'), where('clientId', '==', client.id)));
      const as = aSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => new Date(b.dateAction || 0) - new Date(a.dateAction || 0));
      sett(lang, 'actions')(as);
    } catch (e) { console.log(e); }
    setLoading(false);
  };

  const supprimerVisite = async (visiteId) => {
    try {
      await deleteDoc(doc(db, 'visites', visiteId));
      setVisiteASupprimer(null);
      await charger();
    } catch (e) { alert('Erreur: ' + e.message); }
  };

  const sauvegarder = async () => {
    try {
      await updateDoc(doc(db, 'clients', client.id), form);
      setEditMode(false);
    } catch (e) { alert('Erreur: ' + e.message); }
  };

  const derniereVisite = visites[0];
  const prochaineAction = actions.find(a => a.statut === 'A FAIRE' && a.dateAction && new Date(a.dateAction) >= new Date());
  const actionEnRetard = actions.find(a => a.statut === 'A FAIRE' && a.dateAction && new Date(a.dateAction) < new Date());

  const POTENTIEL_COLORS = {
    Fort: { bg: '#EAF3DE', color: '#3B6D11' },
    Moyen: { bg: '#FAEEDA', color: '#854F0B' },
    Faible: { bg: '#FCEBEB', color: '#A32D2D' },
  };

  const TYPE_COLORS = {
    'Prospect': '#854F0B',
    'Client actif': '#3B6D11',
    'Client inactif': '#888',
    'Nouveau client': '#185FA5',
  };

  return (
    <div style={{ ...styles.app, direction: isRTL ? 'rtl' : 'ltr' }}>
      {/* HEADER */}
      <div style={styles.topbar}>
        <button onClick={onClose} style={styles.backBtn}>←</button>
        <div style={{ flex: 1 }}>
          <div style={styles.topbarTitle}>{client.nom}</div>
          <div style={styles.topbarSub}>{client.type || client.statut} · {client.commune}</div>
        </div>
        {isManager && (
          <button style={styles.editBtn} onClick={() => setEditMode(!editMode)}>
            {editMode ? '✕' : '✎'}
          </button>
        )}
      </div>

      {/* RÉSUMÉ RAPIDE */}
      <div style={styles.resumeBar}>
        <div style={styles.resumeItem}>
          <div style={styles.resumeVal}>{visites.length}</div>
          <div style={styles.resumeLabel}>Visites</div>
        </div>
        <div style={styles.resumeSep}></div>
        <div style={styles.resumeItem}>
          <div style={styles.resumeVal}>{actions.filter(a => a.statut === 'FAIT').length}</div>
          <div style={styles.resumeLabel}>Actions faites</div>
        </div>
        <div style={styles.resumeSep}></div>
        <div style={styles.resumeItem}>
          <div style={{ ...styles.resumeVal, color: actionEnRetard ? '#C0392B' : '#3B6D11' }}>
            {actionEnRetard ? '⚠️' : prochaineAction ? '📅' : '✅'}
          </div>
          <div style={styles.resumeLabel}>{actionEnRetard ? 'En retard' : prochaineAction ? 'Action prévue' : 'À jour'}</div>
        </div>
        <div style={styles.resumeSep}></div>
        <div style={styles.resumeItem}>
          <div style={{ ...styles.resumeVal, fontSize: 12, color: POTENTIEL_COLORS[client.potentiel || 'Moyen']?.color }}>
            {client.potentiel || 'Moyen'}
          </div>
          <div style={styles.resumeLabel}>Potentiel</div>
        </div>
      </div>

      {/* ONGLETS */}
      <div style={styles.tabs}>
        {[
          { id: 'infos', label: t(lang, 'client_info') },
          { id: 'visites', label: `Visites (${visites.length})` },
          { id: 'actions', label: `${t(lang, 'actions')} (${actions.length})` },
          { id: 'carte', label: t(lang, 'client_position') },
        ].map(tab => (
          <div key={t.id} style={{ ...styles.tab, ...(onglet === tab.id ? styles.tabActive : {}) }}
            onClick={() => setOnglet(t.id)}>
            {t.label}
          </div>
        ))}
      </div>

      <div style={styles.content}>

        {/* ONGLET INFOS */}
        {onglet === 'infos' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {editMode ? (
              <div style={styles.editCard}>
                <div style={styles.sectionLabel}>Modifier les informations</div>
                {[
                  { label: 'Nom', key: 'nom' },
                  { label: 'Téléphone', key: 'telephone' },
                  { label: 'Commune', key: 'commune' },
                  { label: 'Wilaya', key: 'wilaya' },
                  { label: 'Adresse', key: 'adresse' },
                ].map(f => (
                  <div key={f.key} style={{ marginBottom: 10 }}>
                    <div style={styles.fieldLabel}>{f.label}</div>
                    <input style={styles.input} value={form[f.key]} onChange={e => setForm(x => ({ ...x, [f.key]: e.target.value }))} />
                  </div>
                ))}
                <div style={{ marginBottom: 10 }}>
                  <div style={styles.fieldLabel}>Type</div>
                  <select style={styles.select} value={form.type} onChange={e => setForm(x => ({ ...x, type: e.target.value }))}>
                    {['Prospect', 'Client actif', 'Client inactif', 'Nouveau client'].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <div style={styles.fieldLabel}>Potentiel</div>
                  <select style={styles.select} value={form.potentiel} onChange={e => setForm(x => ({ ...x, potentiel: e.target.value }))}>
                    {['Faible', 'Moyen', 'Fort'].map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={styles.btnCancel} onClick={() => setEditMode(false)}>{t(lang, 'cancel')}</button>
                  <button style={styles.btnSave} onClick={sauvegarder}>✅ Sauvegarder</button>
                </div>
              </div>
            ) : (
              <>
                <div style={styles.infoCard}>
                  {[
                    { label: 'Statut', val: client.type || client.statut, color: TYPE_COLORS[client.type || client.statut] },
                    { label: 'Potentiel', val: client.potentiel || 'Moyen' },
                    { label: 'Commune', val: client.commune },
                    { label: 'Wilaya', val: client.wilaya },
                    { label: 'Adresse', val: client.adresse },
                    { label: 'Téléphone', val: client.telephone },
                  ].filter(i => i.val).map((info, i) => (
                    <div key={i} style={styles.infoRow}>
                      <span style={styles.infoLabel}>{info.label}</span>
                      <span style={{ ...styles.infoVal, color: info.color || '#1C2833' }}>{info.val}</span>
                    </div>
                  ))}
                </div>

                {/* DERNIÈRE VISITE */}
                {derniereVisite && (
                  <>
                    <div style={styles.sectionLabel}>Dernière visite</div>
                    <div style={styles.visiteCard}>
                      <div style={styles.visiteDateBadge}>
                        {derniereVisite.heureDebut ? new Date(derniereVisite.heureDebut).toLocaleDateString('fr-FR') : ''}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={styles.visiteResultat}>{derniereVisite.resultat}</div>
                        <div style={styles.visiteObjectif}>{derniereVisite.objectifVisite}</div>
                        {derniereVisite.commentaire && <div style={styles.visiteComment}>💬 {derniereVisite.commentaire}</div>}
                      </div>
                    </div>
                  </>
                )}

                {/* PROCHAINE ACTION */}
                {(prochaineAction || actionEnRetard) && (
                  <>
                    <div style={styles.sectionLabel}>Prochaine action</div>
                    <div style={{ ...styles.actionCard, background: actionEnRetard ? '#FCEBEB' : '#EAF3DE' }}>
                      <div style={{ fontSize: 20 }}>{actionEnRetard ? '⚠️' : '📅'}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: actionEnRetard ? '#A32D2D' : '#3B6D11' }}>
                          {(actionEnRetard || prochaineAction).action}
                        </div>
                        <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                          {actionEnRetard ? '⚠️ En retard — ' : ''}{(actionEnRetard || prochaineAction).dateAction}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* GPS */}
                {client.gps && (
                  <button style={styles.mapsBtn}
                    onClick={() => window.open(`https://www.google.com/maps?q=${client.gps.lat},${client.gps.lng}`, '_blank')}>
                    📍 Ouvrir dans Google Maps
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* ONGLET VISITES */}
        {onglet === 'visites' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {loading && <div style={styles.empty}>Chargement...</div>}
            {!loading && visites.length === 0 && (
              <div style={styles.empty}>
                <div style={{ fontSize: 32 }}>📋</div>
                <div style={{ color: '#888', marginTop: 8 }}>Aucune visite enregistrée</div>
              </div>
            )}
            {visites.map((v, i) => (
              <div key={v.id} style={styles.visiteCard}>
                <div style={styles.visiteDateBadge}>
                  {v.heureDebut ? new Date(v.heureDebut).toLocaleDateString('fr-FR') : ''}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={styles.visiteResultat}>{v.resultat || 'Non renseigné'}</div>
                  <div style={styles.visiteObjectif}>{v.objectifVisite}</div>
                  {v.commentaire && <div style={styles.visiteComment}>💬 {v.commentaire}</div>}
                  {v.prochaineAction && <div style={{ fontSize: 11, color: '#185FA5', marginTop: 4 }}>→ {v.prochaineAction}</div>}
                  <div style={{ fontSize: 10, color: '#aaa', marginTop: 4 }}>
                    {v.commercialNom} · {v.duree ? `${v.duree} min` : ''}
                  </div>
                </div>
                {isManager && (
                  <button style={{ background: '#FCEBEB', border: 'none', borderRadius: 6, color: '#A32D2D', fontSize: 11, cursor: 'pointer', padding: '4px 8px', alignSelf: 'flex-start', flexShrink: 0 }}
                    onClick={() => setVisiteASupprimer(v)}>🗑</button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ONGLET ACTIONS */}
        {onglet === 'actions' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {!loading && actions.length === 0 && (
              <div style={styles.empty}>
                <div style={{ fontSize: 32 }}>✅</div>
                <div style={{ color: '#888', marginTop: 8 }}>Aucune action enregistrée</div>
              </div>
            )}
            {actions.map((a, i) => (
              <div key={a.id} style={{ ...styles.actionCard, background: a.statut === 'FAIT' ? '#EAF3DE' : a.dateAction && new Date(a.dateAction) < new Date() ? '#FCEBEB' : '#fff' }}>
                <div style={{ fontSize: 18 }}>{a.statut === 'FAIT' ? '✅' : a.dateAction && new Date(a.dateAction) < new Date() ? '⚠️' : '📅'}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1C2833' }}>{a.action}</div>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                    {a.dateAction} · {a.responsable}
                  </div>
                </div>
                <div style={{ fontSize: 11, padding: '3px 8px', borderRadius: 99, background: a.statut === 'FAIT' ? '#3B6D11' : '#888', color: '#fff' }}>
                  {a.statut === 'FAIT' ? 'Fait' : 'À faire'}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ONGLET POSITION */}
        {onglet === 'carte' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {client.gps ? (
              <>
                <div style={styles.infoCard}>
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>Latitude</span>
                    <span style={styles.infoVal}>{client.gps.lat.toFixed(6)}</span>
                  </div>
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>Longitude</span>
                    <span style={styles.infoVal}>{client.gps.lng.toFixed(6)}</span>
                  </div>
                </div>
                <button style={styles.mapsBtn}
                  onClick={() => window.open(`https://www.google.com/maps?q=${client.gps.lat},${client.gps.lng}`, '_blank')}>
                  📍 Voir sur Google Maps
                </button>
                <button style={{ ...styles.mapsBtn, background: '#E6F1FB', color: '#185FA5' }}
                  onClick={() => window.open(`https://www.google.com/maps/dir//${client.gps.lat},${client.gps.lng}`, '_blank')}>
                  🧭 Itinéraire vers ce client
                </button>
              </>
            ) : (
              <div style={styles.empty}>
                <div style={{ fontSize: 32 }}>📍</div>
                <div style={{ color: '#888', marginTop: 8 }}>Pas de position GPS</div>
                <div style={{ fontSize: 12, color: '#aaa', marginTop: 4 }}>Ajoutez la position depuis le Dispatch</div>
              </div>
            )}
          </div>
        )}

      </div>
      {/* MODAL SUPPRIMER VISITE */}
      {visiteASupprimer && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.55)', zIndex: 100, display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ background: '#fff', width: '100%', maxWidth: 480, margin: '0 auto', borderRadius: '20px 20px 0 0', padding: '20px 16px 30px' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#1C2833', marginBottom: 10 }}>🗑 Supprimer cette visite ?</div>
            <div style={{ fontSize: 13, color: '#444', marginBottom: 6 }}>
              Client : <strong>{visiteASupprimer.clientNom}</strong>
            </div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
              {visiteASupprimer.heureDebut ? new Date(visiteASupprimer.heureDebut).toLocaleDateString('fr-FR') : ''} · {visiteASupprimer.resultat}
            </div>
            <div style={{ fontSize: 12, color: '#A32D2D', background: '#FCEBEB', padding: '8px 12px', borderRadius: 8, marginBottom: 16 }}>
              ⚠️ Action irréversible
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={{ flex: 1, padding: 11, border: '1px solid #ddd', borderRadius: 10, background: '#fff', color: '#666', fontSize: 14, cursor: 'pointer' }}
                onClick={() => setVisiteASupprimer(null)}>Annuler</button>
              <button style={{ flex: 2, padding: 11, background: '#C0392B', border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
                onClick={() => supprimerVisite(visiteASupprimer.id)}>🗑 Confirmer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  app: { maxWidth: 480, margin: '0 auto', minHeight: '100vh', background: '#f5f5f5', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif' },
  topbar: { background: '#1C2833', padding: '1rem 1.25rem', color: '#fff', display: 'flex', alignItems: 'center', gap: 10 },
  backBtn: { background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 32, height: 32, color: '#fff', fontSize: 16, cursor: 'pointer', flexShrink: 0 },
  topbarTitle: { fontSize: 15, fontWeight: 600 },
  topbarSub: { fontSize: 11, opacity: 0.7, marginTop: 2 },
  editBtn: { background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 16, width: 32, height: 32, cursor: 'pointer' },
  resumeBar: { display: 'flex', background: '#fff', borderBottom: '1px solid #eee', padding: '10px 0' },
  resumeItem: { flex: 1, textAlign: 'center' },
  resumeVal: { fontSize: 18, fontWeight: 700, color: '#1C2833' },
  resumeLabel: { fontSize: 10, color: '#888', marginTop: 2 },
  resumeSep: { width: 1, background: '#eee', margin: '4px 0' },
  tabs: { display: 'flex', background: '#fff', borderBottom: '1px solid #eee', overflowX: 'auto' },
  tab: { padding: '10px 14px', fontSize: 12, color: '#888', cursor: 'pointer', borderBottom: '2px solid transparent', whiteSpace: 'nowrap', flexShrink: 0 },
  tabActive: { color: '#1C2833', borderBottomColor: '#1C2833', fontWeight: 600 },
  content: { flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: 10 },
  sectionLabel: { fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 },
  infoCard: { background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  infoRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #f5f5f5' },
  infoLabel: { fontSize: 12, color: '#888' },
  infoVal: { fontSize: 13, fontWeight: 500, color: '#1C2833', textAlign: 'right' },
  visiteCard: { background: '#fff', borderRadius: 10, padding: '12px', display: 'flex', gap: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  visiteDateBadge: { fontSize: 11, color: '#888', minWidth: 60, flexShrink: 0, paddingTop: 2 },
  visiteResultat: { fontSize: 13, fontWeight: 600, color: '#1C2833' },
  visiteObjectif: { fontSize: 11, color: '#888', marginTop: 2 },
  visiteComment: { fontSize: 11, color: '#666', marginTop: 4, fontStyle: 'italic' },
  actionCard: { borderRadius: 10, padding: '12px', display: 'flex', gap: 10, alignItems: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  mapsBtn: { width: '100%', padding: 12, background: '#1C2833', border: 'none', borderRadius: 10, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  empty: { textAlign: 'center', padding: '2rem', color: '#888', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  editCard: { background: '#fff', borderRadius: 10, padding: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  fieldLabel: { fontSize: 12, color: '#666', marginBottom: 4 },
  input: { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, fontFamily: 'system-ui' },
  select: { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14 },
  btnCancel: { flex: 1, padding: 11, border: '1px solid #ddd', borderRadius: 10, background: '#fff', color: '#666', fontSize: 13, cursor: 'pointer' },
  btnSave: { flex: 2, padding: 11, background: '#1C2833', border: 'none', borderRadius: 10, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};
